import classNames from 'classnames';
import omit from 'lodash/omit';
import { FC, MouseEventHandler, useEffect, useRef, useState } from 'react';
import { Form, InputGroup } from 'react-bootstrap';

import { Actionable, ActionableLink, HUIAlert } from 'root/widgets/common-components';

import { EVENT_NAMES, emit } from 'root/widgets/events';
import { captureExceptionSentry, extractDataAutomationId, validateDependencies } from 'root/widgets/utils';

import './enquiry-form-view.scss';
import { EnquiryFormViewProps } from './types';

const requiredDependencies = ['enquiryService'];

const EnquiryFormView: FC<EnquiryFormViewProps> = ({
  intent,
  name,
  email,
  mobileNumber,
  profileLink,
  consents,
  submitButton,
  termsAndPolicy,
  errorMessage,
  dependencies,
  onSuccess,
  context,
}) => {
  validateDependencies(dependencies, requiredDependencies);

  const [selectedIntent, setSelectedIntent] = useState<string>('');
  const [errorText, setErrorText] = useState('');
  const [isLoading, setLoading] = useState(false);
  const consentCheckRef = useRef<Record<string, boolean>>({});

  const shouldDisableSubmitButton =
    (intent?.options && !selectedIntent) || !name.value || !email.value || !mobileNumber.value;

  const handleProfileLinkClick: MouseEventHandler<HTMLElement> = (event) => {
    emit(EVENT_NAMES.SEND_ENQUIRY.MANAGE_PROFILE_ON_CLICK, context);
    profileLink?.onClick?.(event);
  };

  const getIntentClickHandler = (intentKey: string) => () => {
    setSelectedIntent(intentKey);
  };

  const getConsentClickHandler = (consentKey: string) => () => {
    consentCheckRef.current = { ...consentCheckRef.current, [consentKey]: !consentCheckRef.current[consentKey] };
  };

  const captureException = (functionName: string, error: any) => {
    if (!dependencies.sentry) {
      return;
    }

    captureExceptionSentry(dependencies.sentry, EnquiryFormView.name, functionName, error);
  };

  const handleSubmitClick = async () => {
    const data = {
      intent: selectedIntent,
      name: name.value as string,
      email: email.value as string,
      mobileNumber: mobileNumber.value as string,
      consent: consentCheckRef.current,
    };
    const eventContext = { ...context, ...data };

    emit(EVENT_NAMES.SEND_ENQUIRY.SUBMIT_ON_CLICK, eventContext);
    if (!name.value || !email.value || !mobileNumber.value) {
      setErrorText(errorMessage.invalidInput);
      return;
    }

    setErrorText('');
    setLoading(true);

    try {
      await dependencies.enquiryService.sendEnquiry(data);
      emit(EVENT_NAMES.SEND_ENQUIRY.SUBMIT_ON_SUCCESS, eventContext);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      captureException(handleSubmitClick.name, error);
      setErrorText(errorMessage.requestFailed);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!name.value || !email.value || !mobileNumber.value) {
      setErrorText(errorMessage.invalidInput);
    } else {
      setErrorText('');
    }
  }, [email.value, errorMessage.invalidInput, mobileNumber.value, name.value]);

  return (
    <div className="aem-efv-root">
      <div className="aem-efv-body">
        <Form className="d-flex flex-column efv-form">
          {errorText && <HUIAlert variant="warning">{errorText}</HUIAlert>}
          {intent && (
            <Form.Group className="efv-form-group">
              <Form.Label className="mb-1 efv-label">{intent.label}</Form.Label>
              <div className="efv-intent" {...extractDataAutomationId(intent.metadata)}>
                {intent.options.map((option) => (
                  <Actionable
                    key={option.value}
                    variant="outline-secondary"
                    className={classNames('intent-badge', { selected: option.value === selectedIntent })}
                    onClick={getIntentClickHandler(option.value)}
                  >
                    {option.text}
                  </Actionable>
                ))}
              </div>
            </Form.Group>
          )}
          <Form.Group className="efv-form-group">
            <Form.Label className="mb-1 efv-label">
              {name.label}
              {profileLink && <ActionableLink {...profileLink} onClick={handleProfileLinkClick} />}
            </Form.Label>
            <Form.Control
              type="text"
              required
              disabled
              name="name"
              {...omit(name, ['label'])}
              {...extractDataAutomationId(name.metadata)}
            />
          </Form.Group>
          <Form.Group className="efv-form-group">
            <Form.Label className="mb-1 efv-label">{email.label}</Form.Label>
            <Form.Control
              type="text"
              required
              disabled
              name="email"
              {...omit(email, ['label'])}
              {...extractDataAutomationId(email.metadata)}
            />
          </Form.Group>
          <Form.Group className="efv-form-group">
            <Form.Label className="mb-1 efv-label">{mobileNumber.label}</Form.Label>
            <InputGroup>
              {mobileNumber.prefix && <InputGroup.Text>{mobileNumber.prefix}</InputGroup.Text>}
              <Form.Control
                type="text"
                required
                disabled
                name="mobileNumber"
                {...omit(mobileNumber, ['label'])}
                {...extractDataAutomationId(mobileNumber.metadata)}
              />
            </InputGroup>
          </Form.Group>
          {consents &&
            consents.map((consentGroup, index) => (
              <div
                key={`consent-group-${index}`}
                className="efv-consent"
                {...extractDataAutomationId(consentGroup.metadata)}
              >
                {consentGroup.items.map((consent) => (
                  <Form.Check
                    key={consent.name}
                    id={`sign-up-checkbox-${consent.name}`}
                    name={consent.name}
                    type="checkbox"
                    label={consent.text}
                    onChange={getConsentClickHandler(consent.name)}
                  />
                ))}
                {consentGroup.description && <div className="efv-consent-description">{consentGroup.description}</div>}
              </div>
            ))}
          <Actionable
            {...submitButton}
            shouldShowLoading={isLoading}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmitClick}
            disabled={shouldDisableSubmitButton}
          />
        </Form>
      </div>
      {termsAndPolicy && <div className="aem-efv-footer">{termsAndPolicy}</div>}
    </div>
  );
};

EnquiryFormView.displayName = 'EnquiryFormView';

export default EnquiryFormView;
